import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cxcApi } from '../../utils/api';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';
import DocumentDetailModal from '../../components/DocumentDetailModal';
import useAuthStore from '../../store/authStore';
import {
  History, Search, FileText, Undo2, AlertCircle, X, Download,
  FileSpreadsheet, DollarSign, Banknote, Users, Building2,
  Calendar, CreditCard, Receipt, Eye, ChevronRight
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';

export default function HistoricoPagosCxC() {
  const { currentEmisor } = useAuthStore();
  const [cobros, setCobros] = useState([]);
  const [loading, setLoading] = useState(true);
  // FE a ver en modal solo lectura (documentId) desde "Facturas Pagadas"
  const [verFacturaId, setVerFacturaId] = useState(null);
  const [searchCliente, setSearchCliente] = useState('');
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  const { searchRef: clientNameRef, buttonsRef: exportButtonsRef } = useToolbarResponsive([selectedClienteId]);

  // Modal reversar
  const [showReversarModal, setShowReversarModal] = useState(false);
  const [selectedCobro, setSelectedCobro] = useState(null);
  const [motivoReverso, setMotivoReverso] = useState('');
  const [reversando, setReversando] = useState(false);

  // Modal detalle cobro
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [detalleCobro, setDetalleCobro] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [comprobanteUrl, setComprobanteUrl] = useState(null);
  const [comprobanteType, setComprobanteType] = useState(null);

  // Exportando
  const [exportando, setExportando] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const autoAbiertoRef = useRef(false);

  useEffect(() => {
    loadCobros();
  }, []);

  // Al llegar desde una notificación (?cobroId=X): abrir el detalle de ese cobro exacto.
  useEffect(() => {
    if (loading || autoAbiertoRef.current) return;
    const cobroId = searchParams.get('cobroId');
    if (!cobroId) return;
    const cobro = cobros.find(c => String(c.id) === String(cobroId));
    if (cobro) {
      autoAbiertoRef.current = true;
      // Seleccionar el cliente al que se le cobró (la lista agrupa por cédula)
      setSelectedClienteId(cobro.receptorIdentificacion || 'sin-cliente');
      openDetalleModal(cobro);
      // Limpiar el parámetro para que no se reabra al navegar
      const next = new URLSearchParams(searchParams);
      next.delete('cobroId');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cobros, searchParams]);

  const loadCobros = async () => {
    try {
      setLoading(true);
      const response = await cxcApi.listCobros({ limit: 1000 });
      setCobros(response.data.data || []);
    } catch (error) {
      console.error('Error loading cobros:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar cobros por cliente
  const clientesConCobros = useMemo(() => {
    const clienteMap = new Map();

    cobros.forEach(c => {
      const clienteKey = c.receptorIdentificacion || 'sin-cliente';

      if (!clienteMap.has(clienteKey)) {
        clienteMap.set(clienteKey, {
          id: clienteKey,
          nombre: c.receptorNombre || 'Cliente sin nombre',
          identificacion: c.receptorIdentificacion || '',
          cobros: [],
          totalCRC: 0,
          totalUSD: 0,
          cantidadCRC: 0,
          cantidadUSD: 0
        });
      }

      const cliente = clienteMap.get(clienteKey);
      cliente.cobros.push(c);

      const monto = parseFloat(c.montoTotal) || 0;
      if (c.moneda === 'USD') {
        cliente.totalUSD += monto;
        cliente.cantidadUSD++;
      } else {
        cliente.totalCRC += monto;
        cliente.cantidadCRC++;
      }
    });

    // Convertir a array y ordenar alfabéticamente por nombre del cliente
    return Array.from(clienteMap.values())
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }));
  }, [cobros]);

  // Filtrar clientes por busqueda
  const clientesFiltrados = useMemo(() => {
    if (!searchCliente.trim()) return clientesConCobros;
    const search = searchCliente.toLowerCase();
    return clientesConCobros.filter(c =>
      c.nombre.toLowerCase().includes(search) ||
      c.identificacion.toLowerCase().includes(search)
    );
  }, [clientesConCobros, searchCliente]);

  // Cliente seleccionado
  const clienteSeleccionado = useMemo(() => {
    if (!selectedClienteId) return null;
    return clientesConCobros.find(c => c.id === selectedClienteId);
  }, [clientesConCobros, selectedClienteId]);

  // Seleccionar primer cliente por defecto
  useEffect(() => {
    if (clientesFiltrados.length > 0 && !selectedClienteId) {
      setSelectedClienteId(clientesFiltrados[0].id);
    }
  }, [clientesFiltrados, selectedClienteId]);

  const formatMoney = (amount, moneda = 'CRC') => {
    const num = parseFloat(amount) || 0;
    if (moneda === 'USD') {
      return `$ ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₡ ${num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('DD/MM/YYYY');
  };

  const getMedioPagoBadge = (medio) => {
    const medios = {
      'transferencia': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Transferencia' },
      'efectivo': { bg: 'bg-green-100', text: 'text-green-700', label: 'Efectivo' },
      'cheque': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Cheque' },
      'tarjeta': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Tarjeta' },
      'sinpe': { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'SINPE' },
      'saldo_favor': { bg: 'bg-white border border-emerald-500', text: 'text-emerald-700', label: 'Saldo a favor' },
      'otro': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Otro' }
    };
    const m = medios[medio] || medios['otro'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${m.bg} ${m.text}`}>
        {m.label}
      </span>
    );
  };

  const openReversarModal = (cobro) => {
    setSelectedCobro(cobro);
    setMotivoReverso('');
    setShowReversarModal(true);
  };

  const openDetalleModal = async (cobro) => {
    setDetalleCobro(null);
    setComprobanteUrl(null);
    setComprobanteType(null);
    setShowDetalleModal(true);
    setLoadingDetalle(true);

    try {
      // Cargar detalle completo del cobro con sus facturas
      const response = await cxcApi.getCobro(cobro.id);
      setDetalleCobro(response.data.data);

      // Si tiene comprobante, cargarlo
      if (response.data.comprobanteStoragePath) {
        try {
          const compResponse = await cxcApi.getCobroComprobante(cobro.id);
          const blob = compResponse.data;
          const url = URL.createObjectURL(blob);
          setComprobanteUrl(url);
          setComprobanteType(blob.type);
        } catch (err) {
          console.error('Error cargando comprobante:', err);
        }
      }
    } catch (error) {
      console.error('Error cargando detalle:', error);
      toast.error('Error al cargar detalle del cobro');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const closeDetalleModal = () => {
    if (comprobanteUrl) {
      URL.revokeObjectURL(comprobanteUrl);
    }
    setShowDetalleModal(false);
    setDetalleCobro(null);
    setComprobanteUrl(null);
    setComprobanteType(null);
  };

  const handleReversar = async () => {
    if (!selectedCobro || !motivoReverso.trim()) {
      toast.error('Debe ingresar un motivo');
      return;
    }

    try {
      setReversando(true);
      await cxcApi.reversarCobro(selectedCobro.id, motivoReverso);
      toast.success('Cobro reversado exitosamente');
      setShowReversarModal(false);
      loadCobros();
    } catch (error) {
      console.error('Error reversando cobro:', error);
      toast.error(error.response?.data?.error || 'Error al reversar cobro');
    } finally {
      setReversando(false);
    }
  };

  const handleExportExcel = async () => {
    if (!clienteSeleccionado) return;
    try {
      setExportando(true);
      const receptorId = clienteSeleccionado.cobros[0]?.receptorId;
      const response = await cxcApi.exportarHistorial('excel', { receptorId });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Historial_${clienteSeleccionado.nombre.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('Error al exportar');
    } finally {
      setExportando(false);
    }
  };

  const handleExportPDF = async () => {
    if (!clienteSeleccionado) return;
    try {
      setExportando(true);
      const receptorId = clienteSeleccionado.cobros[0]?.receptorId;
      const response = await cxcApi.exportarHistorial('pdf', { receptorId });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Historial_${clienteSeleccionado.nombre.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('Error al exportar');
    } finally {
      setExportando(false);
    }
  };

  // Contar facturas totales del cliente
  const getTotalFacturas = (cliente) => {
    return cliente.cobros.reduce((sum, c) => sum + (c.cantidadFacturas || 1), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 outline-total">
      {/* Layout Master-Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[var(--alto-contenido)]">

        {/* Panel Izquierdo - Lista de Clientes */}
        <div className="lg:col-span-1 bg-white rounded-xl border flex flex-col overflow-hidden">
          {/* Busqueda */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar cliente..."
                value={searchCliente}
                onChange={(e) => setSearchCliente(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Lista de Clientes */}
          <div className="flex-1 overflow-y-auto">
            {clientesFiltrados.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p>No hay clientes con cobros registrados</p>
              </div>
            ) : (
              <div className="divide-y">
                {clientesFiltrados.map((cliente) => (
                  <button
                    key={cliente.id}
                    onClick={() => setSelectedClienteId(cliente.id)}
                    className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                      selectedClienteId === cliente.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{cliente.nombre}</p>
                        <p className="text-xs text-gray-500">{cliente.identificacion}</p>
                      </div>
                      <span className="flex-shrink-0 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        {cliente.cobros.length}
                      </span>
                    </div>

                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t bg-gray-50">
            <p className="text-xs text-gray-500">{clientesFiltrados.length} clientes</p>
          </div>
        </div>

        {/* Panel Derecho - Historial del Cliente */}
        <div className="lg:col-span-2 bg-white rounded-xl border flex flex-col overflow-hidden">
          {!clienteSeleccionado ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <History className="h-16 w-16 mx-auto text-gray-300 mb-3" />
                <p className="font-medium">Seleccione un cliente</p>
                <p className="text-sm">para ver su historial de cobros</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header del cliente — responsive: nombre absolute izq, botones centrados */}
              <div className="p-4 border-b bg-white relative">
                <div ref={clientNameRef} className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3 z-10">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Building2 className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{clienteSeleccionado.nombre}</h2>
                    <p className="text-sm text-gray-500">{clienteSeleccionado.identificacion}</p>
                  </div>
                </div>
                {/* Botones de Exportar — centrados al header (responsive con hook) */}
                <div ref={exportButtonsRef} className="flex items-center justify-center gap-2 py-2">
                  <button
                    onClick={handleExportExcel}
                    disabled={exportando}
                    className="flex items-center gap-2 px-3 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    <FileSpreadsheet size={16} />
                    Excel
                  </button>
                  <button
                    onClick={handleExportPDF}
                    disabled={exportando}
                    className="flex items-center gap-2 px-3 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    <Download size={16} />
                    PDF
                  </button>
                </div>
              </div>

              {/* Cards de Totales */}
              <div className="p-4 border-b">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* CRC */}
                  <div className="bg-white rounded-lg border border-green-300 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Banknote className="h-4 w-4 text-green-600" />
                      <span className="text-xs font-medium text-green-800">Colones</span>
                    </div>
                    <p className="text-lg font-bold text-green-700 ">
                      {formatMoney(clienteSeleccionado.totalCRC, 'CRC')}
                    </p>
                    <p className="text-xs text-green-600">{clienteSeleccionado.cantidadCRC} cobros</p>
                  </div>

                  {/* USD */}
                  <div className="bg-white rounded-lg border border-blue-300 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                      <span className="text-xs font-medium text-blue-800">Dolares</span>
                    </div>
                    <p className="text-lg font-bold text-blue-700 ">
                      {formatMoney(clienteSeleccionado.totalUSD, 'USD')}
                    </p>
                    <p className="text-xs text-blue-600">{clienteSeleccionado.cantidadUSD} cobros</p>
                  </div>

                  {/* Total Cobros */}
                  <div className="bg-white rounded-lg border border-purple-300 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Receipt className="h-4 w-4 text-purple-600" />
                      <span className="text-xs font-medium text-purple-800">Total Cobros</span>
                    </div>
                    <p className="text-lg font-bold text-purple-700 ">
                      {clienteSeleccionado.cobros.length}
                    </p>
                    <p className="text-xs text-purple-600">Transacciones</p>
                  </div>

                  {/* Facturas Pagadas */}
                  <div className="bg-white rounded-lg border border-amber-300 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-amber-600" />
                      <span className="text-xs font-medium text-amber-800">Facturas</span>
                    </div>
                    <p className="text-lg font-bold text-amber-700 ">
                      {getTotalFacturas(clienteSeleccionado)}
                    </p>
                    <p className="text-xs text-amber-600">Pagadas</p>
                  </div>
                </div>
              </div>

              {/* Tabla de Cobros */}
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Fecha</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Facturas</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Medio</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Referencia</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Monto</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {clienteSeleccionado.cobros
                      .sort((a, b) => new Date(b.fechaCobro) - new Date(a.fechaCobro))
                      .map((cobro) => (
                          <tr key={cobro.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-900">{formatDate(cobro.fechaCobro)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                                <FileText className="h-3 w-3" />
                                {cobro.cantidadFacturas || 1}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {getMedioPagoBadge(cobro.medioPago)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {cobro.numeroComprobante || '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`text-sm font-bold ${
                                cobro.moneda === 'USD' ? 'text-blue-600' : 'text-gray-600'
                              }`}>
                                {formatMoney(cobro.montoTotal, cobro.moneda)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => openDetalleModal(cobro)}
                                  className="p-1.5 rounded transition-colors text-blue-500 hover:bg-blue-50"
                                  title="Ver detalle del cobro"
                                >
                                  <Eye className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => openReversarModal(cobro)}
                                  className="p-1.5 text-orange-500 hover:bg-orange-50 rounded transition-colors"
                                  title="Reversar cobro"
                                >
                                  <Undo2 className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal Reversar */}
      {showReversarModal && selectedCobro && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <h3 className="text-lg font-semibold">Reversar Cobro</h3>
              </div>
              <button onClick={() => setShowReversarModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  Esta accion revertira el cobro de <strong>{formatMoney(selectedCobro.montoTotal, selectedCobro.moneda)}</strong> y
                  actualizara el saldo pendiente de {selectedCobro.cantidadFacturas || 1} factura(s).
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p><strong>Fecha:</strong> {formatDate(selectedCobro.fechaCobro)}</p>
                <p><strong>Cliente:</strong> {selectedCobro.receptorNombre}</p>
                <p><strong>Facturas:</strong> {selectedCobro.cantidadFacturas || 1}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo del reverso *
                </label>
                <textarea
                  value={motivoReverso}
                  onChange={(e) => setMotivoReverso(e.target.value)}
                  rows={3}
                  placeholder="Indique el motivo por el cual se reversa este cobro..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowReversarModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReversar}
                  disabled={reversando || !motivoReverso.trim()}
                  className="flex-1 px-4 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 font-medium"
                >
                  {reversando ? 'Procesando...' : 'Confirmar Reverso'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle del Cobro */}
      {showDetalleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detalle del Cobro</h3>
                <p className="text-sm text-gray-500">
                  {detalleCobro ? `${detalleCobro.cantidadFacturas || 1} factura(s) pagada(s)` : 'Cargando...'}
                </p>
                {detalleCobro?.montoRecibidoCRC > 0 && (
                  <span className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-600"></span>
                    </span>
                    Pago recibido en colones (₡)
                  </span>
                )}
              </div>
              <button
                onClick={closeDetalleModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
              {loadingDetalle ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="mt-3 text-gray-500">Cargando detalle...</p>
                  </div>
                </div>
              ) : detalleCobro ? (
                <>
                  {/* Informacion del Cobro */}
                  <div className="p-6 border-b bg-gradient-to-r from-green-50 to-emerald-50">
                    {/* Fila 1: Fecha, Medio, Monto, Facturas */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Fecha de Cobro */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-4 w-4 text-blue-600" />
                          <span className="text-xs font-medium text-gray-500">Fecha de Cobro</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">{dayjs(detalleCobro.fechaCobro).format('DD/MM/YYYY')}</p>
                        <p className="text-sm text-gray-500">{dayjs(detalleCobro.createdAt || detalleCobro.fechaCobro).format('HH:mm')} hrs</p>
                      </div>

                      {/* Medio de Pago */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border">
                        <div className="flex items-center gap-2 mb-1">
                          <CreditCard className="h-4 w-4 text-purple-600" />
                          <span className="text-xs font-medium text-gray-500">Medio de Pago</span>
                        </div>
                        <div className="mt-1">
                          {getMedioPagoBadge(detalleCobro.medioPago)}
                        </div>
                      </div>

                      {/* Monto del Cobro */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-medium text-gray-500">{detalleCobro.montoRecibidoCRC > 0 ? 'Recibido en colones' : 'Monto del Cobro'}</span>
                        </div>
                        {detalleCobro.montoRecibidoCRC > 0 ? (
                          <>
                            <p className="text-xl font-bold text-gray-800">{formatMoney(detalleCobro.montoRecibidoCRC, 'CRC')}</p>
                            <p className="text-xs text-gray-500 mt-0.5">Factura: {formatMoney(detalleCobro.montoTotal, detalleCobro.moneda)}</p>
                          </>
                        ) : (
                          <p className={`text-xl font-bold ${detalleCobro.moneda === 'USD' ? 'text-blue-600' : 'text-gray-600'}`}>
                            {formatMoney(detalleCobro.montoTotal, detalleCobro.moneda)}
                          </p>
                        )}
                      </div>

                      {/* Facturas */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="h-4 w-4 text-amber-600" />
                          <span className="text-xs font-medium text-gray-500">Facturas</span>
                        </div>
                        <p className="text-xl font-bold text-amber-700">
                          {detalleCobro.cantidadFacturas || 1}
                        </p>
                      </div>
                    </div>

                    {/* Fila 2: Registrado por, Referencia, Banco */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      {/* Registrado por */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="h-4 w-4 text-gray-600" />
                          <span className="text-xs font-medium text-gray-500">Registrado por</span>
                        </div>
                        <p className="text-base font-medium text-gray-900">
                          {detalleCobro.createdByName || 'Sistema'}
                        </p>
                      </div>

                      {/* Referencia */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border">
                        <div className="flex items-center gap-2 mb-1">
                          <Receipt className="h-4 w-4 text-gray-600" />
                          <span className="text-xs font-medium text-gray-500">Referencia</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900">
                          {detalleCobro.numeroComprobante || '-'}
                        </p>
                      </div>

                      {/* Banco */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="h-4 w-4 text-gray-600" />
                          <span className="text-xs font-medium text-gray-500">Banco</span>
                        </div>
                        <p className="text-base font-medium text-gray-900">
                          {detalleCobro.banco || '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Lista de Facturas Pagadas */}
                  <div className="p-6 border-b">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Facturas Pagadas con este Cobro
                    </h4>

                    {detalleCobro.facturas && detalleCobro.facturas.length > 0 ? (
                      <div className="bg-gray-50 rounded-lg border overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Factura</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Fecha</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-600">Total Factura</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-600">Monto Aplicado</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {detalleCobro.facturas.map((factura, idx) => (
                              <tr key={idx} className="bg-white">
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                                    <span className="font-mono font-medium">{factura.consecutivo}</span>
                                    {factura.documentoId && (
                                      <button
                                        type="button"
                                        onClick={() => setVerFacturaId(factura.documentoId)}
                                        className="p-1 text-gray-400 hover:text-primary-600 rounded"
                                        title="Ver factura (solo lectura)"
                                      >
                                        <Eye className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-gray-500">
                                  {formatDate(factura.fechaEmision)}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-700">
                                  {formatMoney(factura.totalFactura, detalleCobro.moneda)}
                                </td>
                                <td className="px-4 py-2 text-right font-semibold text-green-600">
                                  {formatMoney(factura.montoPagado, detalleCobro.moneda)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                        <p>No hay detalle de facturas disponible</p>
                      </div>
                    )}
                  </div>

                  {/* Detalle del cobro: si entró en colones (FE en USD), desglose completo en ₡
                      (cuadra a ₡0, retención incluida en colones). Si no, se muestran las notas. */}
                  {detalleCobro.montoRecibidoCRC > 0 ? (() => {
                    const fmt = (n) => '₡' + (n || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    const tc = detalleCobro.tipoCambio || 1;
                    const esUSD = detalleCobro.moneda === 'USD';
                    const retencionCRC = esUSD ? (detalleCobro.retencionRenta || 0) * tc : (detalleCobro.retencionRenta || 0);
                    const recibidoCRC = detalleCobro.montoRecibidoCRC || 0;
                    const residual = -(detalleCobro.diferencialCambiario || 0); // >0 pérdida, <0 ganancia
                    const totalCRC = recibidoCRC + retencionCRC + residual;
                    const esPerdida = residual >= 0;
                    return (
                      <div className="p-6 border-b">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Detalle del cobro (en colones)
                        </h4>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-1.5 text-sm max-w-xl">
                          <div className="flex justify-between">
                            <span className="text-gray-700">Total factura (T/C emisión)</span>
                            <span className="font-mono">{fmt(totalCRC)}</span>
                          </div>
                          {retencionCRC > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">(−) Retención 2% renta (crédito fiscal)</span>
                              <span className="font-mono text-purple-700">−{fmt(retencionCRC)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">(−) Recibido en colones</span>
                            <span className="font-mono">−{fmt(recibidoCRC)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className={esPerdida ? 'text-red-700' : 'text-green-700'}>
                              ({esPerdida ? '−' : '+'}) {esPerdida ? 'Pérdida' : 'Ganancia'} por diferencial cambiario
                            </span>
                            <span className={`font-mono ${esPerdida ? 'text-red-700' : 'text-green-700'}`}>
                              {esPerdida ? '−' : '+'}{fmt(Math.abs(residual))}
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-amber-300 pt-1.5 font-semibold text-gray-900">
                            <span>Diferencia (cuadra a ₡0)</span>
                            <span className="font-mono">{fmt(totalCRC - retencionCRC - recibidoCRC - residual)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })() : detalleCobro.notas ? (
                    <div className="p-6 border-b">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Detalle del cobro
                      </h4>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-1.5">
                        {String(detalleCobro.notas).split(' | ').map((parte, i) => {
                          const idx = parte.indexOf(':');
                          const label = idx >= 0 ? parte.slice(0, idx) : parte;
                          const valor = idx >= 0 ? parte.slice(idx + 1).trim() : '';
                          const esPerdida = /[Pp]érdida|[Pp]erdida/.test(valor);
                          const esGanancia = /[Gg]anancia/.test(valor);
                          const colorClass = esPerdida ? 'text-red-700' : esGanancia ? 'text-green-700' : 'text-gray-900';
                          return (
                            <div key={i} className="flex justify-between gap-4 text-sm">
                              <span className="text-gray-600">{label}{valor ? ':' : ''}</span>
                              {valor && <span className={`font-mono font-medium text-right ${colorClass}`}>{valor}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {/* Comprobante adjunto (si existe) */}
                  {detalleCobro.comprobanteStoragePath && (
                    <div className="p-4 bg-gray-100">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Comprobante Adjunto
                      </h4>
                      {comprobanteUrl ? (
                        <div className="flex items-center justify-center">
                          {comprobanteType?.includes('pdf') ? (
                            <iframe
                              src={`${comprobanteUrl}#toolbar=0&navpanes=0`}
                              className="w-full h-[50vh] rounded-lg border bg-white"
                              title="Comprobante PDF"
                            />
                          ) : (
                            <img
                              src={comprobanteUrl}
                              alt="Comprobante"
                              className="max-w-full max-h-[50vh] rounded-lg shadow-lg"
                            />
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-32 text-gray-500 bg-white rounded-lg border">
                          <p>No se pudo cargar el comprobante</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mensaje si no hay comprobante */}
                  {!detalleCobro.comprobanteStoragePath && (
                    <div className="p-6 text-center text-gray-500 bg-gray-50">
                      <FileText className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm">Este cobro no tiene comprobante adjunto</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <p>No se encontro el detalle del cobro</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={closeDetalleModal}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal solo lectura para ver la FE desde Facturas Pagadas */}
      {verFacturaId && (
        <DocumentDetailModal
          documentId={verFacturaId}
          emisor={currentEmisor}
          onClose={() => setVerFacturaId(null)}
        />
      )}
    </div>
  );
}
